import { z } from 'zod';

/**
 * One element of `Content.parts` from a Google GenAI response, as forwarded
 * by ADK.
 *
 * An example of the parts array structure:
 *
 * "parts": [
 *   { "text": "Here's what I found:" },
 *   { "functionCall": { "name": "search_quotes", "args": { "q": "stoic" } } }
 * ]
 */
export const AgentEventPart = z.object({
  text: z.string().optional(),
  thought: z.boolean().optional(),
  functionCall: z
    .object({
      id: z.string().optional(),
      name: z.string(),
      args: z.record(z.string(), z.unknown()).optional(),
    })
    .optional(),
  functionResponse: z
    .object({
      id: z.string().optional(),
      name: z.string(),
      response: z.record(z.string(), z.unknown()).optional(),
    })
    .optional(),
});
export type AgentEventPart = z.infer<typeof AgentEventPart>;

export const AgentEventContent = z.object({
  role: z.enum(['model', 'user']).optional(),
  parts: z.array(AgentEventPart).optional(),
});
export type AgentEventContent = z.infer<typeof AgentEventContent>;

/**
 * A successful ADK `Event` as serialized by the FastAPI.
 */
export const AdkEvent = z.object({
  content: AgentEventContent.optional(),
  partial: z.boolean().optional(),
  author: z.string().optional(),
  id: z.string().optional(),
  invocationId: z.string().optional(),
  finishReason: z.string().optional(),
});
export type AdkEvent = z.infer<typeof AdkEvent>;

/**
 * Failure Event surfaced through the SSE stream.
 * The `detail` shape is for ADK errors, the 'error' shape is for Django errors.
 */
export const AgentStreamError = z.union([
  z.object({ detail: z.string() }),
  z.object({ error: z.string() }),
]);
export type AgentStreamError = z.infer<typeof AgentStreamError>;

/**
 * Represents a payload from the SSE stream from `/agents/run/`:
 * either a successful ADK event or a stream-level error envelope.
 */
export const AgentStreamPayload = z.union([AgentStreamError, AdkEvent]);
export type AgentStreamPayload = z.infer<typeof AgentStreamPayload>;
